<script setup lang="ts">
import {
  Button, Form, FormItem, Input, Textarea, Select,
} from 'ant-design-vue';
import type { Rule } from 'ant-design-vue/es/form/interface';
import type { CombinedError } from '@urql/vue';

// import FileContentSelect from '@/components/FileContent/Input/FileContentSelect.vue';
import FileAiPromptSelect from '@/components/FileAiPrompts/Input/FileAiPromptSelect.vue';
import {
  type FileContentFragmentFragment,
  type FileAiPromptFragmentFragment,
  type FileFragmentFragment,
  SnowflakeModelEnum,
} from '@/graphql/generated/graphql';
import enumToArray from '~/util/enumToArray';
import { required, requiredObject } from '@/util/rules';
import AlertError from '@/ui/NoDataUrql/AlertError.vue';


export interface FormValues {
  fileContent: FileContentFragmentFragment;
  fileAiPrompt: FileAiPromptFragmentFragment;
  modelDriver: SnowflakeModelEnum;
}

defineProps<{
  file: FileFragmentFragment;
  fetching?: boolean;
  error?: CombinedError | null;
}>();

const emit = defineEmits<{
  close: [];
  submit: [payload: FormValues];
}>();

const formState = reactive<Partial<FormValues>>({});

const rules: Partial<Record<keyof FormValues, Rule | Rule[]>> = {
  fileContent: requiredObject('Select a file content row for this file.'),
  modelDriver: required('Model driver is required.'),
  fileAiPrompt: requiredObject('File AI Prompt is required.'),
};


const onFinish = (values: FormValues) => {
  emit('submit', values);
};
</script>

<template>
  <div>
    <p class="mb-4 text-gray-600">
      Pick the file content row for this file, enter the model driver and prompt, then run the review.
      You can load prompt text from a saved File AI Prompt or edit it below.
    </p>
    <AlertError v-if="error" :error="error" class="mb-4" />
    <Form
      ref="formRef"
      :model="formState"
      :rules="rules"
      layout="vertical"
      @finish="onFinish"
    >
      <FormItem label="File ID">
        <Input
          :value="file.id"
          readonly
        />
      </FormItem>
      <FormItem name="fileContent" label="File content row">
        <FileContentServiceInputSelect
          v-model:value="formState.fileContent"
          :file="file"
        />
      </FormItem>
      <FormItem name="fileAiPrompt" label="File AI Prompt">
        <FileAiPromptSelect v-model:value="formState.fileAiPrompt" />
        <p class="mt-1 text-xs text-gray-500">
          Fills the prompt field from the selected prompt's text.
        </p>
      </FormItem>
      <FormItem name="modelDriver" label="Model driver">
        <Select
          v-model:value="formState.modelDriver"
          :disabled="fetching"
          :options="enumToArray(SnowflakeModelEnum)"
        />
      </FormItem>
      <FormItem name="prompt" label="Prompt">
        <Textarea
          :value="formState.fileAiPrompt?.prompt ?? ''"
          :rows="8"
          placeholder="Full prompt text sent to the model"
          readonly
        />
      </FormItem>
      <div class="flex justify-end gap-2">
        <Button html-type="button" :disabled="fetching" @click="emit('close')">
          Close
        </Button>
        <Button
          type="primary"
          html-type="submit"
          :loading="fetching"
        >
          Run review
        </Button>
      </div>
    </Form>
  </div>
</template>
